/**
 * useTradeCategories Hook
 *
 * Fetches trade categories from the database for use in dropdowns and filters.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface TradeCategory {
  id: string;
  name: string;
  description: string | null;
  icon_name: string | null;
}

interface TradeCategoriesResponse {
  success: boolean;
  data: TradeCategory[];
}

interface TradeCategoryNamesResponse {
  success: boolean;
  data: string[];
}

/**
 * Hook to fetch all trade categories with full details
 *
 * @returns Trade categories array with id, name, description, etc.
 */
export function useTradeCategories() {
  return useQuery({
    queryKey: ['tradeCategories'],
    queryFn: async (): Promise<TradeCategory[]> => {
      const response = await api.get<TradeCategoriesResponse>('/api/lookup/trade-categories');
      return response.data;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - trade categories rarely change, refetchQueries() forces updates
    gcTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });
}

/**
 * Hook to fetch just trade category names as a string array
 * Useful for simple select/dropdown components
 *
 * @returns Array of trade category names
 */
export function useTradeCategoryNames() {
  return useQuery({
    queryKey: ['tradeCategories', 'names'],
    queryFn: async (): Promise<string[]> => {
      const response = await api.get<TradeCategoryNamesResponse>('/api/lookup/trade-categories/names');
      return response.data;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - trade categories rarely change, refetchQueries() forces updates
    gcTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });
}

export default useTradeCategories;
